import {PopOver} from 'common/components/popover/popover';
import {termDescriptionContext} from 'features/terms/term-provider';
import {FC, ReactNode, useState, useContext, useRef, MouseEventHandler, KeyboardEventHandler, useId} from 'react';
import {createUseStyles} from 'react-jss';
import {Theme} from 'styles/theme';
import {useTranslation} from 'react-i18next';

const useStyles = createUseStyles<string, {overrideNormalTextColor: boolean}, Theme>(
  theme => ({
    textBtn: ({overrideNormalTextColor}) => ({
      '@media screen': {
        background: 'none',
        padding: 0,
        border: 'none',
        color: overrideNormalTextColor ? theme.colors.link.normal : 'inherit',
        textDecoration: 'underline',
        textDecorationStyle: 'dashed',
        textDecorationThickness: '1px',
        textDecorationColor: 'currentCol',
        lineHeight: '1.25rem',
        userSelect: 'text',
        cursor: 'unset',
        textAlign: 'left',
        letterSpacing: 'var(--ds-body-md-letter-spacing)',
        fontWeight: 'inherit',

        '&:hover': {
          color: theme.colors.link.hover.fg,
          background: theme.colors.link.hover.bg,
        },

        '&:focus, &:focus-visible': {
          color: theme.colors.link.focus.fg,
          background: theme.colors.link.focus.bg,
          outline: 'none',
        },

        '&:active': {
          color: theme.colors.link.active.fg,
          background: theme.colors.link.active.bg,
        },
      },

      '@media print': {
        display: 'contents',
      },
    }),
    textBtnNotVisual: {
      display: 'contents',
      color: 'inherit',
      userSelect: 'text',
      cursor: 'unset',
      textAlign: 'left',
    },
  }),
  {name: 'DescribedText'}
);

export const DescribedText: FC<{
  children: ReactNode;
  screenreaderText?: string;
  description?: ReactNode;
  descWidth?: number;
  overrideNormalTextColor?: boolean;
}> = ({children, description, overrideNormalTextColor = true, descWidth, screenreaderText}) => {
  const styles = useStyles({overrideNormalTextColor});
  const [openState, setOpenState] = useState<'hidden' | 'focused' | 'hovered'>('hidden');
  const {
    descriptions: {visible},
  } = useContext(termDescriptionContext);
  const id = useId();

  const btnRef = useRef(null);

  const onMouseEnter = () => {
    if (openState === 'hidden') {
      setOpenState('hovered');
    }
  };

  const onMouseLeave = () => {
    if (openState === 'hovered') {
      setOpenState('hidden');
    }
  };

  const onClick: MouseEventHandler = ev => {
    ev.stopPropagation();
    if (openState !== 'focused') {
      setOpenState('focused');
    } else {
      setOpenState('hidden');
    }
  };

  const onBlur = () => setOpenState('hidden');

  const onKeyDown: KeyboardEventHandler = ev => {
    if (openState === 'hidden') {
      if (ev.code === 'Space') {
        setOpenState('focused');
        ev.preventDefault();
      }
    } else if (ev.code === 'Space' || ev.code === 'Escape') {
      setOpenState('hidden');
      ev.preventDefault();
    }
  };

  const {t} = useTranslation(['common']);

  return (
    <span id={id} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <span aria-describedby={`${id}-popover`} className='sr-only'>
        {screenreaderText ?? children}
      </span>
      <button
        className={visible ? styles.textBtn : 'sr-only'}
        data-testid='described-text-btn'
        aria-expanded={openState !== 'hidden'}
        aria-controls={`${id}-popover`}
        aria-live='assertive'
        ref={btnRef}
        onClick={onClick}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        aria-label={t('common:show_term_explanation')}
      >
        {children}
      </button>
      {!visible && (
        <div aria-hidden='true' className={styles.textBtnNotVisual}>
          {children}
        </div>
      )}
      <span className={visible ? '' : 'sr-only'} data-testid='popover-wrapper'>
        <PopOver id={`${id}-popover`} isOpen={openState !== 'hidden'} attachedRef={btnRef} width={descWidth}>
          {description}
        </PopOver>
      </span>
    </span>
  );
};
